import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import Autocomplete, {createFilterOptions} from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ListItem from '@mui/material/ListItem'
import TextField from '@mui/material/TextField'
import {green, grey} from '@mui/material/colors'
import {useGetEntitySearchV2} from 'app/hooks/entities/useGetEntitySearchV2'
import {IConceptFE} from 'interfaces/IConceptFE'
import {ISavedSearchFE} from 'interfaces/ISavedSearchFE'
import {debounce} from 'lodash'
import React, {useState} from 'react'
import {useDispatch} from 'react-redux'
import {updateSavedNotificationSearch} from 'redux/actions/savedSearchActions'
import {AppDispatch} from 'redux/store'
import toastr from 'toastr'
import {useToolbarContext} from '../toolbar/ToolbarProvider'

const filter = createFilterOptions<IConceptFE | string>()

export default function ConceptsAutoCompleteNotify({
  search,
  parentOwned,
  placeholder,
  setParentValue,
  onConfirm,
  onCancel,
}: {
  search: ISavedSearchFE
  parentOwned: boolean
  placeholder?: string
  setParentValue?: React.Dispatch<React.SetStateAction<IConceptFE | string | undefined>>
  onConfirm?: () => void
  onCancel?: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const dispatch = useDispatch<AppDispatch>()
  const {setIsDirty} = useToolbarContext()
  const [value, setValue] = useState<IConceptFE | string | null>(null)
  const [inputValue, setInputValue] = useState<string>('')
  const [debouncedValue, setDebouncedValue] = useState<string>('')

  const optionConcepts: any = useGetEntitySearchV2<any>({
    entity: 'concept',
    search_str: debouncedValue,
    page: 1,
    items_per_page: 20,
  })?.data as unknown as {
    count: number
    next: boolean
    prev: boolean
    records: IConceptFE[]
  }[]

  const onConfirmAddingConcept = async () => {
    if (!search || !search.id) {
      return
    }
    //check if concept or keyword
    const vObj = typeof value === 'object' ? value : undefined
    if (vObj) {
      search.concepts = [
        ...(search.concepts ?? []),
        {
          id: vObj.id,
          name: vObj.name,
          qid: vObj.qid,
          score: vObj.score,
          aliases: vObj.aliases,
          __lock: 'include',
          __weight: 1,
        },
      ]
      dispatch(
        updateSavedNotificationSearch({
          id: search.id,
          search: search,
          originalSavedSearch: search,
        })
      )

      setIsDirty(true)
    } else {
      if (value && typeof value === 'string') {
        if (search?.keywords?.indexOf(value) === -1) {
          search.keywords = [...(search.keywords ?? []), value]
          dispatch(
            updateSavedNotificationSearch({
              id: search.id,
              search: search,
              originalSavedSearch: search,
            })
          )

          setIsDirty(true)
        } else {
          toastr.error(`keyword filter already exists`)
        }
      }
      // toastr.info(`concept is currently being ignored`)
    }
    //  toastr.error(`concept  already exists`)
  }

  const debouncedSave = React.useCallback(
    debounce((newValue) => setDebouncedValue(newValue), 800),
    []
  )
  const updateValue = (newValue: string) => {
    setInputValue(newValue)
    debouncedSave(newValue)
  }

  const onCloseCancel = (b: boolean) => {
    setValue(null)
    parentOwned && onCancel && onCancel(false)
  }
  const onAddConfirm = () => {
    if (parentOwned) {
      onConfirm && onConfirm()
    } else {
      onConfirmAddingConcept()
    }
    setValue(null)
  }
  React.useEffect(() => {
    if (parentOwned && setParentValue && value) {
      setParentValue(value)
    }
  }, [value, setParentValue])

  const disabled = React.useMemo(() => {
    if (value && typeof value === 'object' ? value.name : value) {
      return false
    } else {
      return true
    }
  }, [value])

  return (
    <Box display={'flex'} flex={1} paddingBottom={3}>
      <Autocomplete
        size='small'
        value={value}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            //this is freetext not selected from autocomplete list
            setValue(newValue)
          } else if (newValue && typeof newValue === 'object' && newValue?.name?.includes('Add')) {
            //THIS IS KEYWORD OPTION
            setValue(newValue.id ?? '')
          } else {
            //this is concept option
            setValue(newValue)
          }
        }}
        filterOptions={(options: Array<IConceptFE | string>, params: any) => {
          const filtered = filter(options, params)

          const {inputValue} = params
          // Suggest the creation of a new value
          //if isExisting is used - then concepts can't be added as keywords because they exist as an option, not urrently filtering, just adding
          //const isExisting = options.some((option) => inputValue === option.concept_text)
          if (inputValue !== '') {
            //filtered.unshift(`Add "${inputValue}" as keyword filter`)
            filtered.unshift({
              id: inputValue,
              name: `Add "${inputValue}" as keyword filter`,
              __weight: 1,
              __lock: 'include',
            })
          }

          return filtered
        }}
        selectOnFocus
        handleHomeEndKeys
        id='free-solo-with-text-demo'
        options={debouncedValue && optionConcepts?.records ? optionConcepts?.records : []}
        getOptionLabel={(option: any) => {
          // Value selected with enter, right from the input
          if (typeof option === 'string') {
            return option
          }
          // Add "xxx" option created dynamically
          if (option) {
            return option.name
          }
          return option.name
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          updateValue(newInputValue)
        }}
        sx={{
          flex: 6,
          borderRadius: 0,
        }}
        freeSolo
        renderOption={(props, option: any) => (
          <ListItem
            sx={{
              padding: '5px 12px',
              display: 'flex',
              '& span': {
                flexGrow: 1,
                padding: 1,
              },
              '& span:hover': {
                backgroundColor: grey[300],
                cursor: 'pointer',
                '& svg': {
                  color: green[900],
                },
              },
              '& svg': {
                color: grey[300],
              },
            }}
            {...props}
          >
            <span style={{display: 'flex', justifyContent: 'space-between'}}>
              {option.name ?? option}
              {''}
              <CheckCircleIcon fontSize='medium' />
            </span>
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0px',
              },
              '& .MuiAutocomplete-inputRoot': {
                borderRadius: '0px',
              },
            }}
            {...params}
            label={placeholder}
          />
        )}
      />
      <Button
        variant='contained'
        endIcon={<AddIcon />}
        onClick={() => onAddConfirm()}
        sx={{flex: 1, borderRadius: 0}}
        disabled={disabled}
      >
        Add
      </Button>
      {parentOwned && (
        <Button
          variant='contained'
          onClick={() => onCloseCancel(false)}
          sx={{flex: 1, borderRadius: 0}}
          color={'inherit'}
        >
          <CloseIcon />
        </Button>
      )}
    </Box>
  )
}
